import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect, useState } from 'react';
import { TrendingUp, Link2, Globe, FolderOpen } from 'lucide-react';

const AnimatedNumber = ({ value, duration = 2 }) => {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView) {
      let start = 0;
      const end = value;
      const incrementTime = (duration * 1000) / end;
      const timer = setInterval(() => {
        start += 1;
        setCount(start);
        if (start >= end) clearInterval(timer);
      }, incrementTime);
      return () => clearInterval(timer);
    }
  }, [inView, value, duration]);

  return <span ref={ref}>{count}</span>;
};

export const DashboardMockup = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const kpiData = [
    { icon: FolderOpen, label: 'Total Projects', value: 12, color: '#3B82F6' },
    { icon: Link2, label: 'Published Links', value: 147, color: '#22C55E' },
    { icon: Globe, label: 'Unique Domains', value: 89, color: '#8B5CF6' },
    { icon: TrendingUp, label: 'Success Rate', value: 76, suffix: '%', color: '#EC4899' },
  ];

  const tableData = [
    { site: 'medium.com', da: 96, status: 'Published', type: 'Guest Post' },
    { site: 'dev.to', da: 87, status: 'Published', type: 'Profile' },
    { site: 'hashnode.dev', da: 72, status: 'Submitted', type: 'Blog' },
    { site: 'linkedin.com', da: 98, status: 'Published', type: 'Article' },
    { site: 'quora.com', da: 93, status: 'Pending', type: 'Answer' },
  ];

  const statusColors = {
    Published: 'status-published',
    Submitted: 'status-submitted',
    Pending: 'status-pending',
    Rejected: 'status-rejected',
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className="dashboard-mockup w-full max-w-4xl mx-auto animate-float"
      data-testid="dashboard-mockup"
    >
      {/* Dashboard Header */}
      <div className="dashboard-header px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <span className="text-white/60 text-sm font-mono">app.searchefy.com/dashboard</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"></div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="p-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpiData.map((kpi, index) => (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
              className="kpi-card p-4"
              data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <kpi.icon size={18} style={{ color: kpi.color }} />
                <span className="text-white/60 text-xs">{kpi.label}</span>
              </div>
              <div className="text-2xl font-bold text-white">
                <AnimatedNumber value={kpi.value} />
                {kpi.suffix}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Status Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-white/60">
            <span>Campaign Progress</span>
            <span>147 / 193 links processed</span>
          </div>
          <div className="status-bar">
            <motion.div
              className="flex h-full"
              initial={{ width: 0 }}
              animate={inView ? { width: '100%' } : {}}
              transition={{ duration: 1.5, ease: 'easeOut' }}
            >
              <div className="status-segment bg-green-500" style={{ width: '55%' }}></div>
              <div className="status-segment bg-blue-500" style={{ width: '20%' }}></div>
              <div className="status-segment bg-yellow-500" style={{ width: '15%' }}></div>
              <div className="status-segment bg-red-500" style={{ width: '10%' }}></div>
            </motion.div>
          </div>
          <div className="flex gap-4 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span className="text-white/60">Published (55%)</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              <span className="text-white/60">Submitted (20%)</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
              <span className="text-white/60">Pending (15%)</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              <span className="text-white/60">Rejected (10%)</span>
            </span>
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Website</th>
                <th>DA</th>
                <th>Type</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <motion.tr
                  key={row.site}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
                  data-testid={`table-row-${index}`}
                >
                  <td className="text-white font-medium">{row.site}</td>
                  <td className="text-white/80">{row.da}</td>
                  <td className="text-white/60">{row.type}</td>
                  <td>
                    <span className={`status-badge ${statusColors[row.status]}`}>
                      {row.status}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardMockup;
